/**
 * How the cells of a Word table are set: shading, ink, weight and alignment.
 *
 * mammoth turns a document into HTML and keeps what HTML has words for: a
 * heading, a list, bold. A cell's shading, its text colour and its alignment
 * are not among them, so a table that was a blue-headed statement in Word
 * arrives as a grey grid. This reads those off the document's own XML, which
 * sits in the same file as what mammoth reads and is otherwise untouched.
 *
 * Two sources, in order of precedence. A cell's own formatting, written on
 * the cell and on its runs, wins. Under it, the table's style may shade and
 * embolden its first row (Word's "header row" option), and that is read from
 * the style sheet, because a table built with one of Word's own designs has
 * no formatting of its own at all: it is all in the style.
 *
 * Only top-level tables, in document order, because that is what the HTML
 * conversion yields as tables; a table nested inside a cell is text there.
 * And only the cells the conversion yields: a cell continuing a vertical
 * merge is skipped here as it is skipped there, or every look after it in
 * the row would land one cell to the left.
 *
 * Pure. XML text in, looks out.
 */

import type { CellLook, LookGrid } from '@/features/import/styles'

const WORD_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'
const WHITE = '#ffffff'
const BLACK = '#000000'

/** The bit in a table's `tblLook` value that turns its header row on. */
const FIRST_ROW_BIT = 0x0020

function parse(xml: string): Element {
  return new DOMParser().parseFromString(xml, 'application/xml').documentElement
}

/** The direct children with this local name, whatever the prefix. */
function children(parent: Element, name: string): Element[] {
  return Array.from(parent.children).filter((child) => child.localName === name)
}

function child(parent: Element | undefined, name: string): Element | undefined {
  return parent ? children(parent, name)[0] : undefined
}

/** A Word attribute, which lives in the main namespace under the `w:` prefix. */
function attr(element: Element | undefined, name: string): string | null {
  if (!element) return null
  return element.getAttributeNS(WORD_NS, name) ?? element.getAttribute(`w:${name}`)
}

/** Six hex digits, lowercased with a hash, or nothing. `auto` is nothing. */
function hexOf(value: string | null): string | undefined {
  return value && /^[0-9a-fA-F]{6}$/.test(value.trim())
    ? `#${value.trim().toLowerCase()}`
    : undefined
}

/** Whether a toggle element like `<w:b/>` is on. Present means on unless it says not. */
function isOn(toggle: Element | undefined): boolean {
  if (!toggle) return false
  const value = attr(toggle, 'val')
  return value !== '0' && value !== 'false' && value !== 'off'
}

function readAlign(paragraph: Element | undefined): CellLook['align'] {
  const value = attr(child(child(paragraph, 'pPr'), 'jc'), 'val')
  if (value === 'left' || value === 'start') return 'left'
  if (value === 'right' || value === 'end') return 'right'
  if (value === 'center') return 'center'
  return undefined
}

/** The runs of a cell that hold any text, in order. */
function textRuns(cell: Element): Element[] {
  const runs: Element[] = []
  for (const paragraph of children(cell, 'p')) {
    for (const run of children(paragraph, 'r')) {
      const holds = children(run, 't').some((t) => (t.textContent ?? '').trim() !== '')
      if (holds) runs.push(run)
    }
  }
  return runs
}

/**
 * What a cell says about itself, laid over what its table's style said.
 *
 * A cell's own white shading or black ink takes the style's off rather than
 * being kept: both are Word's way of writing "the default", and the report's
 * default is to follow the document.
 */
function cellLook(cell: Element, fromStyle: CellLook | undefined): CellLook | undefined {
  const look: CellLook = { ...fromStyle }

  const fill = hexOf(attr(child(child(cell, 'tcPr'), 'shd'), 'fill'))
  if (fill === WHITE) delete look.fill
  else if (fill) look.fill = fill

  const align = readAlign(children(cell, 'p')[0])
  if (align) look.align = align

  const runs = textRuns(cell)
  if (runs.some((run) => isOn(child(child(run, 'rPr'), 'b')))) look.strong = true

  const coloured = runs.map((run) => hexOf(attr(child(child(run, 'rPr'), 'color'), 'val')))
  const ink = coloured.find((colour) => colour !== undefined)
  if (ink === BLACK) delete look.ink
  else if (ink) look.ink = ink

  return Object.keys(look).length > 0 ? look : undefined
}

/** Whether a cell only continues a merge from the row above, and so is not drawn. */
function continuesMerge(cell: Element): boolean {
  const merge = child(child(cell, 'tcPr'), 'vMerge')
  return merge !== undefined && attr(merge, 'val') !== 'restart'
}

/** Whether a table asks for its style's header row. On unless it says not. */
function wantsHeaderRow(table: Element): boolean {
  const look = child(child(table, 'tblPr'), 'tblLook')
  const flag = attr(look, 'firstRow')
  if (flag !== null) return flag !== '0' && flag !== 'false'
  const bits = attr(look, 'val')
  return bits === null || (parseInt(bits, 16) & FIRST_ROW_BIT) !== 0
}

/**
 * What a table style does to its first row, by style id.
 *
 * Read from the style's own conditional formatting rather than from any of
 * its parents: the designs Word ships all write their header row directly,
 * and following `basedOn` would mean resolving a whole inheritance chain to
 * recover a shading that is nearly always right here.
 */
function headerRowLooks(stylesXml: string | null): ReadonlyMap<string, CellLook> {
  const found = new Map<string, CellLook>()
  if (!stylesXml) return found

  for (const style of children(parse(stylesXml), 'style')) {
    const id = attr(style, 'styleId')
    if (!id || attr(style, 'type') !== 'table') continue

    const firstRow = children(style, 'tblStylePr').find((one) => attr(one, 'type') === 'firstRow')
    if (!firstRow) continue

    const look: CellLook = {}
    const fill = hexOf(attr(child(child(firstRow, 'tcPr'), 'shd'), 'fill'))
    if (fill && fill !== WHITE) look.fill = fill
    const runs = child(firstRow, 'rPr')
    if (isOn(child(runs, 'b'))) look.strong = true
    const ink = hexOf(attr(child(runs, 'color'), 'val'))
    if (ink && ink !== BLACK) look.ink = ink

    if (Object.keys(look).length > 0) found.set(id, look)
  }

  return found
}

/** The top-level tables of a document body, in order. */
function topLevelTables(body: Element): Element[] {
  const tables: Element[] = []
  const walk = (element: Element): void => {
    for (const one of Array.from(element.children)) {
      if (one.localName === 'tbl') tables.push(one)
      else walk(one)
    }
  }
  walk(body)
  return tables
}

/**
 * The looks of every top-level table in a document, in document order.
 *
 * Empty for anything that is not a document, so a file the HTML conversion
 * could read but this could not still imports as it always did.
 */
export function readDocumentLooks(
  documentXml: string | null,
  stylesXml: string | null,
): readonly LookGrid[] {
  if (!documentXml) return []
  const body = child(parse(documentXml), 'body')
  if (!body) return []

  const styles = headerRowLooks(stylesXml)

  return topLevelTables(body).map((table) => {
    const styleId = attr(child(child(table, 'tblPr'), 'tblStyle'), 'val')
    const header = styleId && wantsHeaderRow(table) ? styles.get(styleId) : undefined

    return children(table, 'tr').map((row, at) =>
      children(row, 'tc')
        .filter((cell) => !continuesMerge(cell))
        .map((cell) => cellLook(cell, at === 0 ? header : undefined)),
    )
  })
}
